import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { inject } from 'mobx-react'
import * as React from 'react'
import { pagedList } from 'src/viewModels/pagedList'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import {
  Delete,
  InsertDriveFile,
  OpenInNew,
  Download,
  Archive,
} from '@mui/icons-material'
import { IStores } from '../Stores'
import ReactPaginate from 'react-paginate'
import { Helmet } from 'react-helmet'
import { formatDate } from 'src/utils/Formatter'
import { DocumentSearchRequest } from 'src/viewModels/DocumentSearchRequest'
import {
  searchDocuments,
  downloadPatientDocuments,
  downloadPatientDocumentsAsZip,
} from 'src/services/PatientService'
import UploadModal from './UploadModal'
import { DocumentSummaryResult } from 'src/viewModels/DocumentSummaryResult'
import { useParams } from 'react-router-dom'
import { DocumentCategory } from 'src/utils/DocumentCategory'
import { DocumentType } from 'src/models/enums/DocumentType.enum'
import { IPatient } from 'src/Definitions'
import Moment from 'moment'
import { Upload } from 'mdi-material-ui'
import DeletePatientDocumentModal from './DeletePatientDocumentModal/DeletePatientDocumentModal'
import SortedTableCell, { sortedTableHeaderSx } from 'src/workflow/SortedTableCell'

interface IClaimsAcquisitionProps {
  patientId?: string
  isModalOpen?: boolean
  getPatientById?: (id: string) => Promise<void>
  patient?: IPatient
  openUploadModal?: (patientId: string) => void
  setUpdateParentStateAfterModal: (parentfunction?: () => void) => void
}

class PatientDocuments extends React.Component<IClaimsAcquisitionProps> {
  public state = {
    componentNames: new Array<{ id: string; description: string }>(),
    gridData: new pagedList().items as DocumentSummaryResult[],
    searchFileName: '',
    searchDescription: '',
    searchDateStart: '',
    searchDateEnd: '',
    searchCategory: '',
    searchType: '',
    searchEOC: '',
    page: 1,
    pageCount: 0,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 50,
    gridLoading: false,
    sortColumn: '',
    sortOrder: 'asc',
    isDeletePatientDocumentModalOpen: false,
    selectedDocument: undefined as unknown as DocumentSummaryResult,
    selectedDocumentIds: [] as string[],
  }

  public async componentDidMount() {
    this.props.setUpdateParentStateAfterModal(this.getDocumentData)
    this.props.getPatientById!(this.props.patientId!).then(() => this.getDocumentData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private mapGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={13} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((doc, index) => (
        <TableRow key={index} className="gridPadding">
          <TableCell padding="checkbox">
            <Checkbox
              checked={this.state.selectedDocumentIds.includes(doc.documentId)}
              onChange={() => this.handleSelectDocument(doc.documentId)}
            />
          </TableCell>
          <TableCell>{doc.fileName}</TableCell>
          <TableCell>{doc.description}</TableCell>
          <TableCell>{doc.categoryDescription}</TableCell>
          <TableCell>{doc.typeDescription}</TableCell>
          <TableCell>{doc.episodeOfCareNumberOrLegacyId}</TableCell>
          <TableCell style={{ whiteSpace: 'nowrap' }}>{doc.createdBy}</TableCell>
          <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
            {doc.fileDate ? formatDate(doc.fileDate.toString()) : ''}
          </TableCell>
          <TableCell>
            <Tooltip title="Open Document in a New Window">
              <OpenInNew
                onClick={() => {
                  window.open(doc.fileUrl)
                }}
                style={{
                  cursor: 'pointer',
                  color: '#29348F',
                  marginTop: 'auto',
                  marginBottom: 'auto',
                }}
              />
            </Tooltip>
          </TableCell>
          <TableCell>
            <Tooltip title="Delete Document">
              <Delete
                onClick={() => {
                  this.setState({
                    selectedDocument: doc,
                    isDeletePatientDocumentModalOpen: true,
                  })
                }}
                style={{
                  cursor: 'pointer',
                  color: 'red',
                  marginTop: 'auto',
                  marginBottom: 'auto',
                }}
              />
            </Tooltip>
          </TableCell>
        </TableRow>
      ))
    }
  }

  private getDocumentData = async () => {
    this.setState({ gridLoading: true })
    const { page, pageSize } = this.state
    const requestData = new DocumentSearchRequest()
    requestData.page = page
    requestData.pageSize = pageSize
    requestData.fileName = this.state.searchFileName
    requestData.description = this.state.searchDescription
    requestData.category = this.state.searchCategory
    requestData.type = this.state.searchType
    requestData.episodeOfCareNumberOrLegacyId = this.state.searchEOC
    requestData.fileDateStart = new Date(this.state.searchDateStart)
    requestData.fileDateEnd = new Date(this.state.searchDateEnd)
    requestData.sorting = this.state.sortColumn
      ? { column: this.state.sortColumn, order: this.state.sortOrder }
      : undefined

    await searchDocuments(this.props.patientId!, requestData).then((response: any) => {
      this.setState(
        {
          gridData: response.items,
          pageCount: response.totalPages,
          totalItems: response.totalItems,
          gridLoading: false,
        },
        () => {
          if (response.totalItems > 0) {
            this.setPaginationOffsetData()
          }
        }
      )
    })
    this.setState({ gridLoading: false })
  }

  private search = (event: any) => {
    this.setState({ page: 1 }, () => this.getDocumentData())
    event.preventDefault()
  }

  private clearSearch = () => {
    var cat_select_box = document.getElementById('categorySelect') as HTMLSelectElement
    if (cat_select_box != undefined) {
      cat_select_box.selectedIndex = 0
    }
    var type_select_box = document.getElementById('typeSelect') as HTMLSelectElement
    if (type_select_box != undefined) {
      type_select_box.selectedIndex = 0
    }
    var start = document.getElementById('fileStart') as HTMLInputElement
    if (start != undefined) {
      start.value = ''
    }
    var end = document.getElementById('fileEnd') as HTMLInputElement
    if (end != undefined) {
      end.value = ''
    }
    this.setState(
      {
        page: 1,
        searchFileName: '',
        searchDescription: '',
        searchDateStart: '',
        searchDateEnd: '',
        searchCategory: '',
        searchType: '',
        searchEOC: '',
        sortColumn: '',
        sortOrder: 'asc',
        pageCount: 0,
        firstRecordIndex: 0,
        lastRecordIndex: 0,
        totalItems: 0,
      },
      () => {
        this.setState({ gridData: [], pageCount: 0, totalItems: 0 })
      }
    )
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getDocumentData())
  }

  private sort = (columnName: string) => {
    this.setState(
      {
        sortColumn: columnName,
        sortOrder: this.state.sortOrder === 'asc' ? 'desc' : 'asc',
        page: 1,
      },
      () => this.getDocumentData()
    )
  }

  private renderSortedHeader = (
    columnName: string,
    propertyName: string,
    align?: 'left' | 'center' | 'right'
  ) => (
    <SortedTableCell
      columnName={columnName}
      propertyName={propertyName}
      align={align}
      order={this.state.sortOrder}
      orderBy={this.state.sortColumn}
      onClick={this.sort}
      reserveSortIconSpace
    />
  )

  private handleFileNameChange = (event: any) => {
    this.setState({ searchFileName: event.target.value })
  }

  private handleEOCChange = (event: any) => {
    this.setState({ searchEOC: event.target.value })
  }

  private handleDescriptionChange = (event: any) => {
    this.setState({ searchDescription: event.target.value })
  }

  private handleFileDateStartChange = (date: any) => {
    this.setState({ searchDateStart: date ? Moment(date).format('YYYY-MM-DD') : '' })
  }

  private handleFileDateEndChange = (date: any) => {
    this.setState({ searchDateEnd: date ? Moment(date).format('YYYY-MM-DD') : '' })
  }

  private setSelectedCategory = (event: SelectChangeEvent<string>) => {
    this.setState({ searchCategory: event.target.value })
  }

  private setSelectedType = (event: SelectChangeEvent<string>) => {
    this.setState({ searchType: event.target.value })
  }

  private handleSelectDocument = (documentId: string) => {
    const { selectedDocumentIds } = this.state
    if (selectedDocumentIds.includes(documentId)) {
      this.setState({
        selectedDocumentIds: selectedDocumentIds.filter((id) => id !== documentId),
      })
    } else {
      this.setState({
        selectedDocumentIds: [...selectedDocumentIds, documentId],
      })
    }
  }

  private handleSelectAll = () => {
    const { gridData, selectedDocumentIds } = this.state
    if (!gridData || gridData.length === 0) {
      return
    }
    if (selectedDocumentIds.length === gridData.length) {
      this.setState({ selectedDocumentIds: [] })
    } else {
      this.setState({
        selectedDocumentIds: gridData.map((doc) => doc.documentId),
      })
    }
  }

  private handleDownloadFirstDocument = async () => {
    const { selectedDocumentIds } = this.state
    if (selectedDocumentIds.length === 0) {
      alert('Please select at least one document to download.')
      return
    }

    try {
      this.setState({ gridLoading: true })
      const blob = await downloadPatientDocuments(
        this.props.patientId!,
        selectedDocumentIds
      )

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'document'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      this.setState({ gridLoading: false })
    } catch (error) {
      console.error('Error downloading document:', error)
      alert('Failed to download document. Please try again.')
      this.setState({ gridLoading: false })
    }
  }

  private handleDownloadAllAsZip = async () => {
    const { selectedDocumentIds } = this.state
    if (selectedDocumentIds.length === 0) {
      alert('Please select at least one document to download.')
      return
    }

    try {
      this.setState({ gridLoading: true })
      const blob = await downloadPatientDocumentsAsZip(
        this.props.patientId!,
        selectedDocumentIds
      )

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `PatientDocuments_${this.props.patient?.lastName}_${this.props.patient?.firstName}_${Moment().format('YYYYMMDD_HHmmss')}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      this.setState({ gridLoading: false })
    } catch (error) {
      console.error('Error downloading documents as ZIP:', error)
      alert('Failed to download documents. Please try again.')
      this.setState({ gridLoading: false })
    }
  }

  public render() {
    const { isDeletePatientDocumentModalOpen, gridLoading, selectedDocument } = this.state
    return (
      <>
        <Helmet>
          <title>
            {'Patient Docs: ' +
              (this.props.patient
                ? this.props.patient.lastName +
                  ', ' +
                  this.props.patient.firstName +
                  ' (' +
                  Moment(this.props.patient?.dateOfBirth).format('MM/DD/YYYY') +
                  ')'
                : '')}
          </title>
        </Helmet>
        <UploadModal />
        <ResponsiveAppBar title={'Patient Documents'} pageIcon={<InsertDriveFile />} />
        {isDeletePatientDocumentModalOpen && (
          <DeletePatientDocumentModal
            open={isDeletePatientDocumentModalOpen}
            close={(reload: boolean) => {
              if (reload) {
                this.getDocumentData()
              }
              this.setState({ isDeletePatientDocumentModalOpen: false })
            }}
            patient={this.props.patient!}
            document={selectedDocument}
          />
        )}

        {gridLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '2',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Paper
          style={{ margin: '20px', padding: '20px', paddingBottom: '20px' }}
          elevation={3}
        >
          <form onSubmit={this.search}>
            <Grid container style={{ margin: '10px' }}>
              <Typography
                color="primary"
                component="h6"
                variant="h6"
                gutterBottom
                style={{ paddingBottom: '10px' }}
              >
                SEARCH CRITERIA [{this.props.patient?.lastName},{' '}
                {this.props.patient?.firstName} (
                {Moment(this.props.patient?.dateOfBirth).format('MM/DD/YYYY')})]
              </Typography>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} md={6} lg={2}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchFileName}
                      onChange={this.handleFileNameChange}
                      label="File Name"
                      name="fileName"
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6} lg={2}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchEOC}
                      onChange={this.handleEOCChange}
                      label="EOC/Legacy"
                      name="eoc"
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item container xs={12} md={12} lg={8} spacing={1}>
                  <Grid item xs={2}>
                    <DatePicker
                      value={
                        this.state.searchDateStart
                          ? Moment(this.state.searchDateStart)
                          : null
                      }
                      onChange={this.handleFileDateStartChange}
                      label="Date Start"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          InputLabelProps: { shrink: true },
                        },
                        openPickerIcon: { fontSize: 'small' },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <strong> - </strong>
                  </Grid>
                  <Grid item xs={2}>
                    <DatePicker
                      value={
                        this.state.searchDateEnd ? Moment(this.state.searchDateEnd) : null
                      }
                      onChange={this.handleFileDateEndChange}
                      label="Date End"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          InputLabelProps: { shrink: true },
                        },
                        openPickerIcon: { fontSize: 'small' },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4} lg={2}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchDescription}
                      onChange={this.handleDescriptionChange}
                      label="Description"
                      name="description"
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={4} md={4} lg={2}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <InputLabel shrink={true} variant="outlined">
                      Category
                    </InputLabel>
                    <Select
                      variant="outlined"
                      input={<OutlinedInput notched label="Category" />}
                      native={true}
                      fullWidth
                      onChange={this.setSelectedCategory}
                      id="categorySelect"
                    >
                      <option value={''}>Select a category</option>
                      <option value={DocumentCategory.Authorization}>
                        Authorization
                      </option>
                      <option value={DocumentCategory.Billing}>Billing</option>
                      <option value={DocumentCategory.Contracts}>Contracts</option>
                      <option value={DocumentCategory.Medical}>Medical</option>
                      <option value={DocumentCategory.Misc}>Miscellaneous</option>
                      <option value={DocumentCategory.PT}>PT</option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={5} md={5} lg={3}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <InputLabel shrink={true} variant="outlined">
                      Type
                    </InputLabel>
                    <Select
                      variant="outlined"
                      input={<OutlinedInput notched label="Type" />}
                      native={true}
                      fullWidth
                      onChange={this.setSelectedType}
                      id="typeSelect"
                    >
                      <option value={''}>Select a type</option>
                      <option value={DocumentType.PatientSurgicalContract}>
                        Agreement for Surgical Services
                      </option>
                      <option value={DocumentType.PatientInjectionContract}>
                        Agreement for Surgical Injection Services
                      </option>
                      <option value={DocumentType.BillOut1500}>Bill-Out 1500</option>
                      <option value={DocumentType.CMS1500}>CMS 1500</option>
                      <option value={DocumentType.Combined1500}>Combined 1500</option>
                      <option value={DocumentType.DME}>DME</option>
                      <option value={DocumentType.FCE}>FCE</option>
                      <option value={DocumentType.WorkHardening}>Work Hardening</option>
                      <option value={DocumentType.InHome}>In-Home</option>
                      <option value={DocumentType.InitialEval}>Initial Eval</option>
                      <option value={DocumentType.InpatientRehab}>Inpatient Rehab</option>
                      <option value={DocumentType.InvoiceInitial}>Initial Invoice</option>
                      <option value={DocumentType.PatientDemographics}>
                        Patient Demographics
                      </option>
                      <option value={DocumentType.InvoiceResend}>Resend Invoice</option>
                      <option value={DocumentType.OccupationalTherapy}>
                        Occupational Therapy
                      </option>
                      <option value={DocumentType.PhysicalTherapy}>
                        Physical Therapy
                      </option>
                      <option value={DocumentType.SurgeryFacility}>
                        Surgery / Facility
                      </option>
                      <option value={DocumentType.TakeoverOfCare}>
                        Takeover of Care
                      </option>
                      <option value={DocumentType.Upload}>User Uploaded</option>
                      <option value={DocumentType.WorkConditioning}>
                        Work Conditioning
                      </option>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              style={{
                marginLeft: '1px',
                paddingBottom: '10px',
                paddingTop: '10px',
              }}
            >
              <Grid item style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button type="submit" color="primary" size="medium" variant="contained">
                  Search
                </Button>
                <Button
                  color="secondary"
                  onClick={this.clearSearch}
                  size="medium"
                  variant="contained"
                >
                  Clear Search
                </Button>
              </Grid>
              <Grid item style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Archive />}
                  onClick={this.handleDownloadAllAsZip}
                  disabled={this.state.selectedDocumentIds.length === 0}
                >
                  Download as ZIP ({this.state.selectedDocumentIds.length})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={this.handleDownloadFirstDocument}
                  disabled={this.state.selectedDocumentIds.length === 0}
                >
                  Download Selected ({this.state.selectedDocumentIds.length})
                </Button>
                <Tooltip title="Upload Document">
                  <Upload
                    style={{
                      cursor: 'pointer',
                      color: '#29348F',
                    }}
                    onClick={() => this.props.openUploadModal!(this.props.patientId!)}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </form>
          <Grid container>
            <Grid item xs={12} style={{ paddingTop: '16px' }}>
              <Table aria-label="simple table" sx={sortedTableHeaderSx}>
                <TableHead>
                  <TableRow className="gridPadding">
                    <TableCell padding="checkbox">
                      <Tooltip
                        title={
                          this.state.gridData &&
                          this.state.gridData.length > 0 &&
                          this.state.selectedDocumentIds.length ===
                            this.state.gridData.length
                            ? 'Deselect All Documents'
                            : 'Select All Documents'
                        }
                      >
                        <Checkbox
                          indeterminate={
                            this.state.selectedDocumentIds.length > 0 &&
                            this.state.gridData &&
                            this.state.selectedDocumentIds.length <
                              this.state.gridData.length
                          }
                          checked={
                            this.state.gridData &&
                            this.state.gridData.length > 0 &&
                            this.state.selectedDocumentIds.length ===
                              this.state.gridData.length
                          }
                          onChange={this.handleSelectAll}
                        />
                      </Tooltip>
                    </TableCell>
                    {this.renderSortedHeader('File Name', 'FileName')}
                    {this.renderSortedHeader('Description', 'Description')}
                    {this.renderSortedHeader('Category', 'CategoryDescription')}
                    {this.renderSortedHeader('Type', 'TypeDescription')}
                    {this.renderSortedHeader(
                      'EOC/Legacy',
                      'EpisodeOfCareNumberOrLegacyId'
                    )}
                    {this.renderSortedHeader('User', 'CreatedBy')}
                    {this.renderSortedHeader('Date', 'FileDate', 'right')}
                    <TableCell>{/* Open Document */}</TableCell>
                    <TableCell>{/* Delete Document */}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>{this.mapGridData()}</TableBody>
              </Table>
              <div className="pagination-row">
                {this.paginationInfo()}
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  onPageChange={this.handlePageChange}
                  pageCount={this.state.pageCount}
                  containerClassName={'pagination'}
                  activeClassName={'active'}
                  //@ts-ignore
                  renderOnZeroPageCount={null}
                />
              </div>
            </Grid>
          </Grid>
        </Paper>
      </>
    )
  }
}

const InjectedClaimsAcquisition = inject<
  IStores,
  IClaimsAcquisitionProps,
  Partial<IClaimsAcquisitionProps>,
  any
>((stores: IStores) => ({
  isModalOpen: stores.patients.isUploadModalOpen,
  getPatientById: stores.patients.getPatientById,
  patient: stores.patients.selectedPatient,
  openUploadModal: stores.patients.openUploadModal,
  setUpdateParentStateAfterModal: stores.patients.setParentUpdateFunction,
}))(PatientDocuments)

const PatientDocumentsWrapper = (props: any) => {
  const params = useParams<{ patientId?: string }>()
  return <InjectedClaimsAcquisition {...props} patientId={params.patientId} />
}

export default PatientDocumentsWrapper
